import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Skeleton,
  Stack,
  Typography
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_BUY_SELL_USER_ITEMS } from "../../constant/Apipath";
import Loader from "../../common/Loader";
import Emptydata from "../common/Emptydata";
import StorefrontIcon from "@mui/icons-material/Storefront";

const SitesBuySellSection = ({ siteId, activeTab }) => {
  const { loginData } = useSelector((state) => state?.main);

  const [buySellItems, setBuySellItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageNr, setPageNr] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const itemsPerPage = 10;

  // Memoized fetch function to prevent unnecessary recreations
  const fetchBuySellItems = useCallback(
    async (pageNumber, isInitial = false) => {
      if (!siteId) return;

      try {
        setLoading(true);
        const payload = {
          PageNr: pageNumber,
          NrOfRecPerPage: itemsPerPage,
          FullSearch: "",
          UserId: loginData?.userInformation?.UserId,
          IncludeRecordNr: true,
          TypeOfObjectReturned: "",
          FetchAllowedRecordsOnly: true,
          SearchList: [{ siteId: siteId }],
          SortList: [{ FieldName: "Id", Direction: "DESC" }]
        };

        const response = await Postrequestcall(
          FETCH_BUY_SELL_USER_ITEMS,
          payload,
          loginData?.accessToken,
          false
        );

        if (response?.status === 200) {
          const items = response?.data?.data || [];
          const recordNr = response?.data?.recordNr || 0;

          console.log("Response received - items:", items.length, "total records:", recordNr);

          setBuySellItems((prev) => {
            const newItems = isInitial ? items : [...prev, ...items];
            console.log("Items updated - total loaded:", newItems.length);
            return newItems;
          });

          // Calculate hasMore: if we got fewer items than itemsPerPage, there's no more
          const hasMoreItems = items.length === itemsPerPage;
          console.log("hasMore set to:", hasMoreItems);
          setHasMore(hasMoreItems);
        }
      } catch (err) {
        console.error("Error fetching BuySell items:", err);
        setError(err?.message || "Failed to load items");
      } finally {
        setLoading(false);
      }
    },
    [siteId, loginData, itemsPerPage]
  );

  // Reset and Initial Fetch
  useEffect(() => {
    if (activeTab === "buy-sell" && siteId) {
      setBuySellItems([]);
      setPageNr(1);
      setHasMore(true);
      fetchBuySellItems(1, true);
    }
  }, [siteId, activeTab, fetchBuySellItems]);

  const handleLoadMore = useCallback(() => {
    setPageNr((prevPage) => {
      const nextPage = prevPage + 1;

      fetchBuySellItems(nextPage, false);
      return nextPage;
    });
  }, [pageNr, hasMore, loading, fetchBuySellItems]);

  const getMainImage = (pictureList) => {
    if (!pictureList || pictureList.length === 0) return null;
    const mainImage = pictureList.find((pic) => pic.main);
    return mainImage?.name || pictureList[0]?.name;
  };

  // Initial Loader
  if (loading && pageNr === 1 && buySellItems.length === 0) {
    return (
      <Box sx={{ width: "100%", p: 2 }}>
        <Stack spacing={2}>
          {[...Array(3)].map((_, idx) => (
            <Card key={idx} sx={{ display: "flex", borderRadius: 1 }}>
              <Skeleton variant="rectangular" width={80} height={80} />
              <CardContent sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="40%" />
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      id="buySellScrollContainer"
      sx={{
        width: "100%",
        maxHeight: "65vh",
        overflowY: "auto",
        position: "relative",
        padding: "10px",
        boxSizing: "border-box"
      }}>
      <InfiniteScroll
        dataLength={buySellItems.length}
        next={handleLoadMore}
        hasMore={hasMore}
        scrollableTarget="buySellScrollContainer"
        loader={<Loader />}
        style={{ overflow: "visible" }}>
        {buySellItems.length === 0 && !loading && pageNr === 1 ? (
          <Box className="empty-data-card" sx={{ mt: 3 }}>
            <Emptydata
              icon={<StorefrontIcon className="emptydata-icon" />}
              firstText="No data found"
            />
          </Box>
        ) : (
          <Stack spacing={2}>
          {buySellItems.map((item, index) => {
            const imageUrl = getMainImage(item.pictureList);
            return (
              <Card
                key={`${item.id}-${index}`}
                sx={{
                  display: "flex",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  cursor: "pointer"
                }}>
                {imageUrl && (
                  <CardMedia
                    component="img"
                    sx={{ width: 80, height: 80, objectFit: "cover" }}
                    image={imageUrl}
                    alt={item.title}
                  />
                )}
                <CardContent sx={{ flex: 1, p: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption"  sx={{color: "primary",  fontWeight: 700 }}>
                    {item.forTrade ? "For Trade" : `$${item.price?.toFixed(2)}`}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
        )}
      </InfiniteScroll>
    </Box>
  );
};

export default SitesBuySellSection;
